import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  FieldDefinitionNode,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {getFieldInfoFromDataFlowEdgeHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {
  ProcessFlowchartEdgeType,
  TaskSchemaNodeSectionType,
  TransitionSource,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {buildTransitionIndexByPTDVPairWithSubsets} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionKeyUtils';
import {Transition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionTypes';
import {areFieldDefinitionsCompatible} from 'schemas/fields/FieldDefinitionCompatibilityUtils';
import {detectCyclesWithNode} from 'util/graph-utils/detectCycles';
import {getAdjacencyMapFromProcessFlowchart} from 'util/graph-utils/getFlowchartAdjacencyMap';
import getLoopIgnoringAncestorsAndEdges from 'util/graph-utils/getLoopIgnoringAncestorsAndEdges';
import {AdjacencyMap} from 'util/graph-utils/GraphUtilConstants';

import {hasMatchingControlFlowEdge} from '../flowchart-viz/horizontal-flowchart/edges/edgeUtils';
import {PLUGIN_REGISTRY_INSTANCE} from '../platform/PluginRegistry';
import {
  ProcessFlowchartErrorType,
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
  MaybeValidationError,
  DataFlowEdgeForValidation,
  ProcessTaskSchemaNodeForValidation,
  FlowchartValidationErrorType,
  ValidationContext,
} from './ProcessFlowchartForValidationTypes';
import {
  ProcessEdgeForValidation,
  ProcessNodeForValidation,
  ProcessFlowchartForValidation,
} from './ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from './ProcessGraphBuilderConstants';
import {
  getEdgeDisplayName,
  getFlowchartNodeFromClientId,
  getFulfillingMapperConfigs,
  getInboundEdges,
  getNodesByClientId,
  getOutboundEdges,
  getTaskNodesDownstreamOfNode,
  getTaskNodesUpstreamOfNode,
  getTemplateNodeFromClientId,
  getTemplateNodeFromSchemaId,
} from './ProcessGraphGetters';

const doesNodeListContainNodeByClientId = (clientId: string, nodeList: Array<{id: string}>): boolean =>
  nodeList.find((node) => node.id === clientId) != null;

/**
 * Returns true if there is at least one configured transition, all configured
 * transitions are optional, and ACM or PCM is enabled. Used to allow the canvas
 * to be empty (only Start/End nodes) when only optional transitions exist.
 */
function hasOnlyOptionalTransitions(
  transitions: Record<string, Transition>,
  isFlowchartModifiableOnCreation = false,
  isFlowchartModifiableAfterCreation = false
): boolean {
  const transitionList = Object.values(transitions);
  const acmOrPcmEnabled = isFlowchartModifiableOnCreation || isFlowchartModifiableAfterCreation;
  return transitionList.length > 0 && transitionList.every((t) => !t.isPlanned) && acmOrPcmEnabled;
}

/**
 * Editor contexts where structural flowchart validation rules apply.
 * Structural rules (like requiring root nodes, single entry points, etc.) only apply
 * when editing a full flowchart, not when editing a single transition.
 */
const STRUCTURAL_VALIDATION_MODES: Array<FlowchartEditorMode> = [
  FlowchartEditorMode.MAIN_CANVAS,
  FlowchartEditorMode.ACM_EDITOR,
  FlowchartEditorMode.PCM_EDITOR,
];

const doesNodeListContainNodeBySchemaId = (schemaId: string, nodeList: Array<{schemaId: string}>): boolean =>
  nodeList.find((node) => node.schemaId === schemaId) != null;

function buildNodeIdToPTDVIdMap(nodes: Array<ProcessNodeForValidation>): Map<string, string> {
  const nodeIdToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isTaskSchemaNode(node) && node.taskDefinitionVersionSpec?.clientId != null) {
      nodeIdToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }
  return nodeIdToPTDVId;
}

/**
 * Checks if a validation node is a task schema node (has task definition version).
 */
function isTaskSchemaNode(node: ProcessNodeForValidation): node is ProcessTaskSchemaNodeForValidation {
  return (
    node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
  );
}

function getFieldFromHandle(
  handleId: string,
  node: ProcessTaskSchemaNodeForValidation
): FieldDefinitionNode | null {
  const fieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(handleId);

  const isOutputNode =
    node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS ||
    fieldClientInfo.sectionType === TaskSchemaNodeSectionType.OUTPUTS;

  const fieldDefinitions = isOutputNode
    ? node.taskDefinitionVersionSpec.outputFieldDefinitions
    : node.taskDefinitionVersionSpec.inputFieldDefinitions;

  const fieldDef = fieldDefinitions?.find(
    (field) => field.fieldClientIdentifier === fieldClientInfo.fieldClientId
  );

  return (fieldDef?.fieldDefinition as FieldDefinitionNode) ?? null;
}

interface ValidationCallback {
  nodeId?: string;
  edgeId?: string;
}

export function getDisplayNameForNode(
  node: ProcessNodeForValidation,
  isFlowchartFirstConfig: boolean
): string {
  if (
    isFlowchartFirstConfig &&
    BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_FIRST_CONFIG_MILESTONE_1 &&
    isTaskSchemaNode(node)
  ) {
    return PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(node.executionType).typeDisplayName;
  }
  // Default type display names if not task schema node or not flowchart-first config
  switch (node.nodeType) {
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
      return 'task';
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      return 'root';
    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS:
      return 'router';
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS:
      return 'stop';
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      return 'end';
    default:
      return 'node';
  }
}

// Edge errors are shown on the destination node, if possible, as the current UI doesn't have a way
// to show errors on edges directly.
export function getValidationErrorForEdge(
  flowchart: ProcessFlowchartForValidation,
  edge: ProcessEdgeForValidation,
  errorMessage: (callbackProps: ValidationCallback) => string,
  edgeErrorType: FlowchartValidationErrorType
): ProcessFlowchartValidationError {
  const destinationNode = getFlowchartNodeFromClientId(flowchart, edge.target);
  const destinationNodeId = destinationNode?.id;
  return {
    object: {
      clientId: destinationNodeId ?? edge.id,
      type: destinationNode ? ProcessFlowchartObjectType.NODE : ProcessFlowchartObjectType.EDGE,
    },
    errorMessage: errorMessage({nodeId: destinationNodeId, edgeId: edge.id}),
    errorType: edgeErrorType,
    blocksPersistence: true,
  };
}

function edgeConnectsNodesInFlowchart(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): boolean {
  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.target);
  return (
    fromNode != null &&
    toNode != null &&
    doesNodeListContainNodeByClientId(fromNode.id, flowchart.nodes) &&
    doesNodeListContainNodeByClientId(toNode.id, flowchart.nodes)
  );
}

export function ensureNodeIsInsideDesignSpace(
  node: ProcessNodeForValidation,
  designSpace: ProcessFlowchartDesignSpace
): MaybeValidationError {
  return node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS &&
    node.taskSchemaId !== designSpace.flowchartSchema.id &&
    !doesNodeListContainNodeBySchemaId(node.taskSchemaId, designSpace.availableNodes)
    ? {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `This node is not available for this flowchart.`,
        errorType: ProcessFlowchartErrorType.NODE_NOT_AVAILABLE,
        blocksPersistence: true,
      }
    : null;
}

export function ensureNodeMappersFulfilled(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace,
  usingFlowchartFirstConfig?: boolean
): MaybeValidationError {
  // Root, router, and end nodes cannot be mapped to. So we don't need to validate that their mappers are fulfilled.
  if (
    node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS ||
    usingFlowchartFirstConfig
  ) {
    return null;
  }

  // The associated template node is guaranteed to exist by ensureNodeIsInsideDesignSpace. But we
  // double-check, in case the tests are out of order
  const templateNode = getTemplateNodeFromSchemaId(designSpace.availableNodes, node.taskSchemaId);
  if (templateNode == null) {
    return null;
  }

  // We don't throw an error if the node hasn't been connected yet (this error will be reported elsewhere)
  const incomingEdges = flowchart.edges.filter((edge) => edge.target === node.id);
  if (incomingEdges.length === 0) {
    return null;
  }

  const fulfillingMapperConfigs = getFulfillingMapperConfigs(node, flowchart, templateNode);

  return fulfillingMapperConfigs != null
    ? null
    : {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `This node is missing required paths.`,
        errorType: ProcessFlowchartErrorType.NODE_MISSING_REQUIRED_PATH,
        blocksPersistence: true,
      };
}

interface EnsureNodeIsConnectedParams {
  node: ProcessNodeForValidation;
  nodes: Array<ProcessNodeForValidation>;
  edges: Array<ProcessEdgeForValidation>;
  usingFlowchartFirstConfig: boolean;
  transitions: Record<string, Transition>;
  isFlowchartModifiableOnCreation?: boolean;
  isFlowchartModifiableAfterCreation?: boolean;
}

export function ensureNodeIsConnected(params: EnsureNodeIsConnectedParams): MaybeValidationError {
  const {
    node,
    nodes,
    edges,
    usingFlowchartFirstConfig,
    transitions,
    isFlowchartModifiableOnCreation,
    isFlowchartModifiableAfterCreation,
  } = params;
  let nodeError = null;
  switch (node.nodeType) {
    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS:
      nodeError = {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'Connect to 1 source and at least 2 destinations to configure this router',
        errorType: ProcessFlowchartErrorType.ROUTER_NOT_CONNECTED,
        blocksPersistence: true,
      };
      break;
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS:
      nodeError = {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'Connect this stop to one router as its source.',
        errorType: ProcessFlowchartErrorType.END_NODE_NOT_CONNECTED,
        blocksPersistence: true,
      };
      break;
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      if (
        !hasOnlyOptionalTransitions(
          transitions,
          isFlowchartModifiableOnCreation,
          isFlowchartModifiableAfterCreation
        )
      ) {
        nodeError = {
          object: {
            clientId: node.id,
            type: ProcessFlowchartObjectType.NODE,
          },
          errorMessage: 'Connect the End to the rest of the flowchart.',
          errorType: ProcessFlowchartErrorType.OUTPUT_NODE_NOT_CONNECTED,
          blocksPersistence: true,
        };
      }
      break;
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      if (
        !hasOnlyOptionalTransitions(
          transitions,
          isFlowchartModifiableOnCreation,
          isFlowchartModifiableAfterCreation
        )
      ) {
        nodeError = {
          object: {
            clientId: node.id,
            type: ProcessFlowchartObjectType.NODE,
          },
          errorMessage: 'Connect the Start to the rest of the flowchart.',
          errorType: ProcessFlowchartErrorType.ROOT_NODE_NOT_CONNECTED,
          blocksPersistence: true,
        };
      }
      break;
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
      nodeError = {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `Connect this ${getDisplayNameForNode(node, usingFlowchartFirstConfig)} to the rest of the flowchart.`,
        errorType: ProcessFlowchartErrorType.TASK_NODE_NOT_CONNECTED,
        blocksPersistence: true,
      };
      break;
    default:
      nodeError = null;
  }
  // We give a special pass to one-node flowcharts
  const nodeIsConnected =
    nodes.length === 1 ||
    edges.find(
      (edge) =>
        (edge.source === node.id || edge.target === node.id) &&
        edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    ) != null;
  return !nodeIsConnected ? nodeError : null;
}

export function ensureFlowchartHasTaskNode(nodes: Array<ProcessNodeForValidation>): MaybeValidationError {
  const taskNode = nodes.find((node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS);
  return taskNode == null
    ? {
        object: null,
        errorMessage: "This flowchart needs a node for the flowchart's task schema.",
        errorType: ProcessFlowchartErrorType.NO_FLOWCHART_TASK_NODE,
        blocksPersistence: true,
        enabledModes: STRUCTURAL_VALIDATION_MODES,
      }
    : null;
}

export function ensureFlowchartHasIntermediateNodes(
  nodes: Array<ProcessNodeForValidation>,
  transitions: Record<string, Transition>,
  isFlowchartModifiableOnCreation = false,
  isFlowchartModifiableAfterCreation = false
): MaybeValidationError {
  const rootNode = nodes.find((node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS);
  if (rootNode == null) {
    return null;
  }
  if (
    !nodes.some((node) => node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS) &&
    // Allow flowcharts that have only optional transitions with no canvas nodes
    !hasOnlyOptionalTransitions(
      transitions,
      isFlowchartModifiableOnCreation,
      isFlowchartModifiableAfterCreation
    )
  ) {
    return {
      object: {
        clientId: rootNode.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: 'Add a node after Start.',
      errorType: ProcessFlowchartErrorType.NO_INTERMEDIATE_NODES,
      blocksPersistence: true,
      enabledModes: STRUCTURAL_VALIDATION_MODES,
    };
  }
  return null;
}

interface EnsureFlowchartHasSingleEntryPointParams {
  nodes: Array<ProcessNodeForValidation>;
  edges: Array<ProcessEdgeForValidation>;
  usingFlowchartFirstConfig: boolean;
  transitions: Record<string, Transition>;
  isFlowchartModifiableOnCreation?: boolean;
  isFlowchartModifiableAfterCreation?: boolean;
}

export function ensureFlowchartHasSingleEntryPoint(
  params: EnsureFlowchartHasSingleEntryPointParams
): Array<ProcessFlowchartValidationError> | null {
  const {
    nodes,
    edges,
    usingFlowchartFirstConfig,
    transitions,
    isFlowchartModifiableOnCreation,
    isFlowchartModifiableAfterCreation,
  } = params;
  const startNodes = nodes.filter(
    (node) =>
      edges.find(({target, type}) => target === node.id && type === ProcessFlowchartEdgeType.CONTROL_FLOW) ==
      null
  );
  return startNodes.length > 1
    ? startNodes
        .filter(
          (startNode) =>
            startNode.nodeType !== ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS &&
            // If the flowchart has only optional transitions, the output node has no incoming edges
            // but is not a real second entry point
            !(
              startNode.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS &&
              hasOnlyOptionalTransitions(
                transitions,
                isFlowchartModifiableOnCreation,
                isFlowchartModifiableAfterCreation
              )
            )
        )
        .map((startNode) => ({
          object: {
            clientId: startNode.id,
            type: ProcessFlowchartObjectType.NODE,
          },
          errorMessage: `Flowcharts can only have 1 starting point. Draw a connection to this ${getDisplayNameForNode(startNode, usingFlowchartFirstConfig)}.`,
          errorType: ProcessFlowchartErrorType.TOO_MANY_START_NODES,
          blocksPersistence: true,
          enabledModes: STRUCTURAL_VALIDATION_MODES,
        }))
    : null;
}

interface EnsureFlowchartHasOutputNodeParams {
  nodes: Array<ProcessNodeForValidation>;
  designSpace: ProcessFlowchartDesignSpace;
}

export function ensureFlowchartHasOutputNode(params: EnsureFlowchartHasOutputNodeParams): {
  error: MaybeValidationError;
  outputNode: ProcessNodeForValidation | null;
} {
  const {nodes, designSpace} = params;
  // If the flowchart schema has outputs, we need an output node, and we need it to have an incoming edge
  const outputNode =
    nodes.find((node) => node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS) ?? null;
  if (designSpace.flowchartSchema.outputSchema != null) {
    if (outputNode == null) {
      return {
        error: {
          object: null,
          errorMessage: "This flowchart needs a node for the flowchart's output schema.",
          errorType: ProcessFlowchartErrorType.NO_OUTPUT_NODE,
          blocksPersistence: true,
          enabledModes: STRUCTURAL_VALIDATION_MODES,
        },
        outputNode,
      };
    }
  }
  return {error: null, outputNode};
}

// This error should not be possible when using the UI
export function ensureEdgeConnectsNodesInFlowchart(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  return !edgeConnectsNodesInFlowchart(edge, flowchart)
    ? getValidationErrorForEdge(
        flowchart,
        edge,
        () =>
          `Path '${getEdgeDisplayName(
            edge,
            flowchart
          )}' connects nodes that haven't been added to the flowchart.`,
        ProcessFlowchartErrorType.EDGE_CONNECTS_UNAVAILABLE_NODES
      )
    : null;
}

export function ensureEdgeBetweenSchemasIsValid(
  fromNodeSchemaId: string,
  toNodeSchemaId: string,
  designSpace: ProcessFlowchartDesignSpace
): boolean {
  return designSpace.availableEdges.some((tEdge) => {
    const fromTemplateNode = getTemplateNodeFromClientId(designSpace.availableNodes, tEdge.fromNodeId);
    const toTemplateNode = getTemplateNodeFromClientId(designSpace.availableNodes, tEdge.toNodeId);

    return fromNodeSchemaId === fromTemplateNode?.schemaId && toNodeSchemaId === toTemplateNode?.schemaId;
  });
}

interface EnsureControlFlowEdgeIsValidParams {
  edge: ProcessEdgeForValidation;
  flowchart: ProcessFlowchartForValidation;
  designSpace: ProcessFlowchartDesignSpace;
  usingFlowchartFirstConfig?: boolean;
}

export function ensureControlFlowEdgeIsValid(
  params: EnsureControlFlowEdgeIsValidParams
): Array<ProcessFlowchartValidationError> | ProcessFlowchartValidationError | null {
  const {edge, flowchart, designSpace, usingFlowchartFirstConfig} = params;

  if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const genericEdgeError = (): ProcessFlowchartValidationError =>
    getValidationErrorForEdge(
      flowchart,
      edge,
      () => `Path '${getEdgeDisplayName(edge, flowchart)}' is not available for this flowchart.`,
      ProcessFlowchartErrorType.EDGE_NOT_AVAILABLE
    );

  // We have to get the schemas for the target edge. This requires a few hops to connect the client IDs.
  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.target);
  if (fromNode == null || toNode == null) {
    return genericEdgeError();
  }
  if (toNode.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    // Any node can have an edge to a router except for the flowchart output node.
    // But we should disable that via the UI
    return fromNode.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ? genericEdgeError()
      : null;
  }

  if (toNode.nodeType === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    const inboundEdges = getInboundEdges(toNode, flowchart.edges);
    const outboundEdges = getOutboundEdges(toNode, flowchart.edges);
    if (fromNode.nodeType !== ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
      return {
        object: {
          clientId: toNode.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'Connect this stop to one router as its source.',
        errorType: ProcessFlowchartErrorType.END_NODE_CONNECTED_TO_NON_ROUTER_NODE,
        blocksPersistence: true,
      };
    } else if (inboundEdges.length > 1) {
      return {
        object: {
          clientId: toNode.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'Connect this stop to one router as its source.',
        errorType: ProcessFlowchartErrorType.END_NODE_CONNECTED_TO_MULTIPLE_SOURCE_NODES,
        blocksPersistence: true,
      };
    } else if (outboundEdges.length > 0) {
      return {
        object: {
          clientId: toNode.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'An end node cannot have outgoing edges.',
        errorType: ProcessFlowchartErrorType.END_NODE_HAS_OUTBOUND_EDGES,
        blocksPersistence: true,
      };
    }
    return null;
  }
  if (fromNode.nodeType === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    return genericEdgeError();
  }

  if (usingFlowchartFirstConfig) {
    return null;
  } else {
    // TODO: Remove this once we have migrated to the new flowchart config editor
    // If the edge is from a router, we use the previous nodes to check that the edge is in the design space.
    // eslint-disable-next-line no-lonely-if
    if (fromNode.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
      const taskNodesBeforeRouter = getTaskNodesUpstreamOfNode(fromNode.id, flowchart);
      for (const taskNode of taskNodesBeforeRouter) {
        assert(
          taskNode.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
            taskNode.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
          'Task nodes upstream of a router node must be task or root nodes'
        );
        if (!ensureEdgeBetweenSchemasIsValid(taskNode.taskSchemaId, toNode.taskSchemaId, designSpace)) {
          return [
            getValidationErrorForEdge(
              flowchart,
              edge,
              () => `The transition from ${taskNode.name} to ${toNode.name} is not available`,
              ProcessFlowchartErrorType.EDGE_NOT_AVAILABLE
            ),
            {
              object: {
                clientId: fromNode.id,
                type: ProcessFlowchartObjectType.NODE,
              },
              errorMessage: `One or more transitions through this router are not configured.`,
              errorType: ProcessFlowchartErrorType.ROUTER_EDGES_NOT_CONFIGURED,
              blocksPersistence: true,
            },
          ];
        }
      }
    } else if (!ensureEdgeBetweenSchemasIsValid(fromNode.taskSchemaId, toNode.taskSchemaId, designSpace)) {
      return genericEdgeError();
    }
  }

  return null;
}

// This represents a technical problem. The UI should of course prevent this error from occurring, but an
// automation in the future may produce duplicate edges.
export function ensureNoDuplicateEdges(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  const errors: Array<ProcessFlowchartValidationError> = [];
  for (const scanEdge of flowchart.edges) {
    const bothEdgesAreControlFlow =
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
      scanEdge.type === ProcessFlowchartEdgeType.CONTROL_FLOW;
    const bothEdgesAreDataFlow =
      edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
      scanEdge.type === ProcessFlowchartEdgeType.DATA_FLOW;
    const edgesHaveSameSourceAndTargetNodes =
      edge.source === scanEdge.source && edge.target === scanEdge.target;
    const isDuplicateControlFlowEdge = bothEdgesAreControlFlow && edgesHaveSameSourceAndTargetNodes;
    const isDuplicateDataFlowEdge =
      bothEdgesAreDataFlow &&
      edgesHaveSameSourceAndTargetNodes &&
      edge.sourceHandle === scanEdge.sourceHandle &&
      edge.targetHandle === scanEdge.targetHandle;

    if (edge.id !== scanEdge.id && (isDuplicateControlFlowEdge || isDuplicateDataFlowEdge)) {
      errors.push(
        getValidationErrorForEdge(
          flowchart,
          edge,
          () => `Path '${getEdgeDisplayName(edge, flowchart)}' is a duplicate.`,
          ProcessFlowchartErrorType.DUPLICATE_EDGES
        )
      );
    }
  }
  return errors.length > 0 ? errors : null;
}

/**
 * This validation checks that for every loop in a flowchart, you can only "enter" at one distinct node.
 * For instance, in the following graph:
 *       2 -->
 *     /      \
 *    1        5 -> R1 -> 7
 *     \      /      \
 *      3 -> 4        6
 *       \           /
 *         <---------
 * there exists a loop (3 -> 4 -> 5 -> R1 -> 6 -> 3) with two entry points (node 3, via 1 -> 3, and node 5,
 * via 2 -> 5), which is disallowed.
 *
 * We disallow this because it complicates ancestor-finding. Because we track ancestry using a DFS that
 * stops when we detect a cycle, in the above graph, we might detect an ancestor of 3 that we would otherwise
 * ignore.
 *
 * If we traverse the path 1 -> 2 -> 5 -> R1 -> 6 -> 3, we set 5 as an ancestor of 3, because no cycle
 * has been detected. However, if we traverse the path 1 -> 3 -> 4 -> 5 -> R1 -> 6 -> 3, we set 3 as an
 * ancestor of 5, and seeing a loop, do not set 5 as an ancestor of 3. For LCA purposes, this would be correct.
 */
export function ensureLoopsHaveOneEntryPoint(
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  if (rootNode == null) {
    return null;
  }
  const adjacencyMap: AdjacencyMap = getAdjacencyMapFromProcessFlowchart(flowchart);
  const {nodeIdsToAncestors} = getLoopIgnoringAncestorsAndEdges(rootNode.id, adjacencyMap);

  const nodesInLoop: Set<string> = new Set();
  // We iterate through each node's ancestors.
  // If node A has an ancestor, node B, who has also detected node A as its ancestor, we error on both node A and node B.
  for (const [nodeId, ancestors] of Object.entries(nodeIdsToAncestors)) {
    if (nodesInLoop.has(nodeId)) {
      // Stop early if we're already erroring on the node
      continue;
    }
    for (const ancestor of ancestors) {
      if (ancestor !== nodeId && nodeIdsToAncestors[ancestor].has(nodeId)) {
        // We ignore the ancestor when it is the node itself
        nodesInLoop.add(nodeId);
        nodesInLoop.add(ancestor);
        break;
      }
    }
  }

  return [...nodesInLoop].map((nodeId) => ({
    object: {
      clientId: nodeId,
      type: ProcessFlowchartObjectType.NODE,
    },
    errorMessage: `Each loop can only have one starting point.`,
    errorType: ProcessFlowchartErrorType.LOOP_HAS_MULTIPLE_ENTRY_POINTS,
    blocksPersistence: true,
  }));
}

export function ensureLoopsContainTaskAndRouterNode(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  if (rootNode == null) {
    return null;
  }

  const adjacencyMap: AdjacencyMap = getAdjacencyMapFromProcessFlowchart(flowchart);

  const nodesByClientId = getNodesByClientId(flowchart);
  const cycles = detectCyclesWithNode(adjacencyMap, node.id);
  for (const cyclicNodes of cycles) {
    const hasRouterNode = Array.from(cyclicNodes).some(
      (clientId) => nodesByClientId[clientId].nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS
    );
    const hasTaskNode = Array.from(cyclicNodes).some(
      (clientId) => nodesByClientId[clientId].nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS
    );
    if (!hasRouterNode || !hasTaskNode) {
      return {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `Each loop must include at least one task schema and one router.`,
        errorType: ProcessFlowchartErrorType.LOOP_MISSING_TASK_OR_ROUTER_NODE,
        blocksPersistence: true,
      };
    }
  }
  return null;
}

export function ensureFlowchartDoesNotExceedMaxNumberOfNodes(
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  if (rootNode == null) {
    return null;
  }
  const numNodes = flowchart.nodes.length;
  return numNodes > BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI
    ? {
        object: {
          clientId: flowchart.id,
          type: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
        },
        errorMessage: `This flowchart has ${numNodes} blocks, which exceeds the limit of ${BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI}.`,
        errorType: ProcessFlowchartErrorType.MAX_NODES_LIMIT_EXCEEDED,
        blocksPersistence: true,
      }
    : null;
}

interface EnsureNodeNameIsNotEmptyParams {
  node: ProcessNodeForValidation;
  usingFlowchartFirstConfig: boolean;
}

// This will be self-healed so we should never encounter this error in real life
export function ensureNodeNameIsNotEmpty(params: EnsureNodeNameIsNotEmptyParams): MaybeValidationError {
  const {node, usingFlowchartFirstConfig} = params;
  if (node.name != null && node.name.length === 0) {
    return {
      object: {
        clientId: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: `Enter a name for this ${getDisplayNameForNode(node, usingFlowchartFirstConfig)}.`,
      errorType: ProcessFlowchartErrorType.UNNAMED_NODE,
      blocksPersistence: true,
    };
  }
  return null;
}

export function ensureDataFlowEdgeHasAssociatedControlFlowEdge(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  assert(edge.type === ProcessFlowchartEdgeType.DATA_FLOW, 'Expected data flow edge');
  // Two cases are valid, first if the data flow edge connects two nodes that are directly connected by a control flow edge.
  if (hasMatchingControlFlowEdge(edge, flowchart)) {
    return null;
  }
  // Second, if the data flow edge connects two nodes that are connected by a router node.
  if (getTaskNodesDownstreamOfNode(edge.source, flowchart).some((node) => node.id === edge.target)) {
    return null;
  }
  const targetNode = getFlowchartNodeFromClientId(flowchart, edge.target);
  assert(targetNode != null, 'Expected target node');
  // Otherwise, we error.
  return [
    // Error to be displayed on the target node
    {
      object: {
        clientId: targetNode.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: 'Field mappings can only flow between connected nodes.',
      errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_WITH_NO_CONTROL_FLOW_EDGE,
      blocksPersistence: true,
    },
    // Error to be displayed on the data flow edge
    {
      object: {
        clientId: edge.id,
        type: ProcessFlowchartObjectType.EDGE,
      },
      errorMessage: 'Field mappings can only flow between connected nodes.',
      errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_WITH_NO_CONTROL_FLOW_EDGE,
      blocksPersistence: true,
    },
  ];
}

interface EnsureDataFlowEdgeSourceIsValidParams {
  edge: ProcessEdgeForValidation;
  flowchart: ProcessFlowchartForValidation;
  usingFlowchartFirstConfig: boolean;
}

export function ensureDataFlowEdgeSourceIsValid(
  params: EnsureDataFlowEdgeSourceIsValidParams
): Array<ProcessFlowchartValidationError> | null {
  const {edge, flowchart, usingFlowchartFirstConfig} = params;
  assert(edge.type === ProcessFlowchartEdgeType.DATA_FLOW, 'Expected data flow edge');
  assert(edge.sourceHandle != null && edge.targetHandle != null, 'Expected source and target handles');
  const edgeTransitionSource = edge.transitionSource;
  if (edgeTransitionSource == null) {
    return null;
  }
  const sourceFieldInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const allowedSectionTypes = [TaskSchemaNodeSectionType.INPUTS, TaskSchemaNodeSectionType.MAP_FROM_START];
  if (
    edgeTransitionSource === TransitionSource.INPUTS &&
    !allowedSectionTypes.includes(sourceFieldInfo.sectionType)
  ) {
    const sourceNode = getFlowchartNodeFromClientId(flowchart, edge.source);
    assert(sourceNode != null, 'Expected source node');
    return [
      {
        object: {
          clientId: edge.target,
          type: ProcessFlowchartObjectType.NODE,
        },
        parentObjects: [
          {
            clientId: edge.id,
            type: ProcessFlowchartObjectType.EDGE,
          },
        ],
        errorMessage: `This ${getDisplayNameForNode(sourceNode, usingFlowchartFirstConfig)}'s transition behavior doesn't match its field mappings. Update the transition behavior or adjust the field mappings.`,
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_SOURCE_TYPE_MISMATCH,
        blocksPersistence: true,
      },
    ];
  }
  return null;
}

/**
 * Validates that data flow edges do not conflict on a target field.
 *
 * In all modes: a single source node cannot have multiple data flow edges mapping to the same
 * target field on the same target node.
 *
 * In single-transition mode: different source nodes (with different PTDVs) cannot
 * have data flow edges mapping to the same target field, because this would create conflicting
 * mapper source configs on the backend. We only check this in single-transition mode because it's
 * only applicable to merging nodes.
 */
export function ensureDataFlowEdgesDoNotConflictOnTargetField(
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> {
  const dataFlowEdges = flowchart.edges.filter(
    (edge): edge is DataFlowEdgeForValidation => edge.type === ProcessFlowchartEdgeType.DATA_FLOW
  );

  // Group edges by (target node, target handle) to find all edges targeting the same field
  const edgesByTargetField = new Map<string, Array<DataFlowEdgeForValidation>>();
  for (const edge of dataFlowEdges) {
    const key = `${edge.target}:${edge.targetHandle}`;
    if (!edgesByTargetField.has(key)) {
      edgesByTargetField.set(key, []);
    }
    edgesByTargetField.get(key)!.push(edge);
  }

  const errors: Array<ProcessFlowchartValidationError> = [];

  for (const [, edgesForTargetField] of edgesByTargetField) {
    if (edgesForTargetField.length <= 1) {
      continue;
    }

    // Check 1 (all modes): same source node maps to the same target field multiple times
    const seenSources = new Set<string>();
    const duplicateSources = new Set<string>();
    for (const edge of edgesForTargetField) {
      if (seenSources.has(edge.source)) {
        duplicateSources.add(edge.source);
      }
      seenSources.add(edge.source);
    }

    if (duplicateSources.size > 0) {
      for (const edge of edgesForTargetField) {
        if (duplicateSources.has(edge.source)) {
          errors.push({
            object: {clientId: edge.id, type: ProcessFlowchartObjectType.EDGE},
            errorMessage: '',
            errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_TARGET_FIELD_COLLISION,
            blocksPersistence: true,
          });
        }
      }
      errors.push({
        object: {clientId: edgesForTargetField[0].target, type: ProcessFlowchartObjectType.NODE},
        errorMessage: 'Transition cannot target the same field multiple times.',
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_TARGET_FIELD_COLLISION,
        blocksPersistence: true,
      });
    }

    // Check 2 (single-transition mode only): different PTDVs map to the same target field
    const sourcePTDVs = new Set<string>();
    for (const edge of edgesForTargetField) {
      const sourceNode = getFlowchartNodeFromClientId(flowchart, edge.source);
      if (sourceNode && isTaskSchemaNode(sourceNode) && sourceNode.taskDefinitionVersionSpec) {
        sourcePTDVs.add(sourceNode.taskDefinitionVersionSpec.clientId);
      }
    }

    if (sourcePTDVs.size > 1) {
      for (const edge of edgesForTargetField) {
        errors.push({
          object: {clientId: edge.id, type: ProcessFlowchartObjectType.EDGE},
          errorMessage: '',
          errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_TARGET_FIELD_COLLISION,
          blocksPersistence: true,
          enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
        });
      }
      errors.push({
        object: {clientId: edgesForTargetField[0].target, type: ProcessFlowchartObjectType.NODE},
        errorMessage: 'Different source task types cannot map to the same target field in a transition.',
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_TARGET_FIELD_COLLISION,
        blocksPersistence: true,
        enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
      });
    }
  }

  return errors;
}

/**
 * Validates that no transition is a subset or superset of another transition targeting the same PTDV.
 * E.g. having both {a, b} -> c and {a, b, d} -> c is invalid because {a, b} is a subset of {a, b, d}.
 *
 * On the main canvas, planned transitions are checked against ALL other transitions (both planned
 * and optional). Errors are reported on the control flow edges and destination node of both subset
 * and superset planned transitions.
 *
 * On the single transition canvas, the current transition (derived from the flowchart's edges) is
 * checked for subset/superset relationships against all other transitions. The flowchart ID is used
 * to identify and exclude the current transition from the comparison set.
 */
export function ensureTransitionsAreNotSubsetOfConfigured(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  const TRANSITION_SUBSET_ERROR_MESSAGE =
    'This transition is a subset of another configured transition. Remove it or merge with the other.';
  const TRANSITION_SUPERSET_ERROR_MESSAGE =
    'This transition is a superset of another configured transition. Remove it or merge with the other.';

  const {transitions, editorMode} = validationContext;
  if (transitions == null || Object.keys(transitions).length === 0) {
    return null;
  }

  const isSingleTransitionCanvas = editorMode === FlowchartEditorMode.SINGLE_TRANSITION_CANVAS;

  let transitionsToCheck: Record<string, Transition>;
  let currentTransitionId: string | null = null;

  // Build list of transitions to check based on the editor mode.
  if (isSingleTransitionCanvas) {
    const transitionTargetsAndSources = getTransitionTargetsAndSources(flowchart);
    if (transitionTargetsAndSources.length !== 1) {
      return null;
    }
    const current = transitionTargetsAndSources[0];
    // In the single transition canvas, the flowchart ID is the ID of the transition being configured.
    currentTransitionId = flowchart.id;
    const derivedTransition: Transition = {
      id: currentTransitionId,
      sourcePTDVIds: current.sourcePTDVIds,
      targetPTDVId: current.targetPTDVId,
      transitionSourcesBySourcePTDVId: {},
      customMappingDataByFieldPair: {},
      isPlanned: false,
    };
    transitionsToCheck = {...transitions, [currentTransitionId]: derivedTransition};
  } else {
    transitionsToCheck = transitions;
  }

  // Check for subset and superset relationships within transitions
  const {subsetTransitionIds, supersetTransitionIds} =
    buildTransitionIndexByPTDVPairWithSubsets(transitionsToCheck);
  if (subsetTransitionIds.length === 0 && supersetTransitionIds.length === 0) {
    return null;
  }

  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);

  // Pre-compute incoming control flow edges per node
  const incomingControlFlowEdgesByNodeId = new Map<string, Array<ProcessEdgeForValidation>>();
  for (const edge of flowchart.edges) {
    if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
      continue;
    }
    if (!incomingControlFlowEdgesByNodeId.has(edge.target)) {
      incomingControlFlowEdgesByNodeId.set(edge.target, []);
    }
    incomingControlFlowEdgesByNodeId.get(edge.target)!.push(edge);
  }

  const errors: Array<ProcessFlowchartValidationError> = [];
  const erroredNodeIds = new Set<string>();

  const flaggedTransitions: Array<{
    transitionIds: Array<string>;
    errorMessage: string;
    errorType: ProcessFlowchartErrorType;
  }> = [
    {
      transitionIds: subsetTransitionIds,
      errorMessage: TRANSITION_SUBSET_ERROR_MESSAGE,
      errorType: ProcessFlowchartErrorType.TRANSITION_IS_SUBSET_OF_CONFIGURED,
    },
    {
      transitionIds: supersetTransitionIds,
      errorMessage: TRANSITION_SUPERSET_ERROR_MESSAGE,
      errorType: ProcessFlowchartErrorType.TRANSITION_IS_SUPERSET_OF_CONFIGURED,
    },
  ];

  // Add errors to nodes / edges for invalid transitions
  for (const {transitionIds, errorMessage, errorType} of flaggedTransitions) {
    for (const transitionId of transitionIds) {
      const transition = transitionsToCheck[transitionId];
      if (transition == null) {
        continue;
      }

      if (isSingleTransitionCanvas) {
        if (transitionId !== currentTransitionId) {
          continue;
        }
      } else if (!transition.isPlanned) {
        continue;
      }

      const sourcePTDVIdSet = new Set(transition.sourcePTDVIds);
      const targetPTDVId = transition.targetPTDVId;
      const enabledModes = [FlowchartEditorMode.MAIN_CANVAS, FlowchartEditorMode.SINGLE_TRANSITION_CANVAS];
      const baseError = {
        errorMessage,
        errorType,
        blocksPersistence: true,
        enabledModes,
      };

      // Find nodes whose PTDV matches the transition's target, then verify
      // the exact transition is present by checking incoming edges.
      for (const node of flowchart.nodes) {
        const nodePTDV = nodeIdToPTDVId.get(node.id);
        if (nodePTDV !== targetPTDVId) {
          continue;
        }

        const incomingEdges = incomingControlFlowEdgesByNodeId.get(node.id) ?? [];
        const matchingEdges = incomingEdges.filter((edge) => {
          const sourcePTDV = nodeIdToPTDVId.get(edge.source);
          return sourcePTDV != null && sourcePTDVIdSet.has(sourcePTDV);
        });

        // Verify all of the transition's source PTDVs are covered by incoming edges
        const coveredSourcePTDVs = new Set(matchingEdges.map((edge) => nodeIdToPTDVId.get(edge.source)!));
        if (![...sourcePTDVIdSet].every((ptdv) => coveredSourcePTDVs.has(ptdv))) {
          continue;
        }

        for (const edge of matchingEdges) {
          errors.push({
            ...baseError,
            object: {
              clientId: edge.id,
              type: ProcessFlowchartObjectType.EDGE,
            },
          });
        }

        if (!erroredNodeIds.has(node.id)) {
          erroredNodeIds.add(node.id);
          errors.push({
            ...baseError,
            object: {
              clientId: node.id,
              type: ProcessFlowchartObjectType.NODE,
            },
          });
        }
      }
    }
  }

  return errors.length > 0 ? errors : null;
}

interface TransitionTargetWithSources {
  targetPTDVId: string;
  targetNodeId: string;
  sourcePTDVIds: Array<string>;
}

/**
 * Returns the targets and their respective sources for a flowchart or transition. A target is a
 * task schema node with incoming control-flow edges from other task schema nodes.
 *
 * This assumes no router nodes are present for current use cases (in the single transitions modal).
 */
function getTransitionTargetsAndSources(
  flowchart: ProcessFlowchartForValidation
): Array<TransitionTargetWithSources> {
  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);

  const transitionTargetsWithSources: Array<TransitionTargetWithSources> = [];

  for (const edge of flowchart.edges) {
    if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
      continue;
    }
    if (nodeIdToPTDVId.has(edge.source) && nodeIdToPTDVId.has(edge.target)) {
      const sourcePTDVId = nodeIdToPTDVId.get(edge.source);
      const targetPTDVId = nodeIdToPTDVId.get(edge.target);
      if (sourcePTDVId != null && targetPTDVId != null) {
        const existingTargetWithSources = transitionTargetsWithSources.find(
          (t) => edge.target === t.targetNodeId
        );
        if (existingTargetWithSources != null) {
          existingTargetWithSources.sourcePTDVIds.push(sourcePTDVId);
        } else {
          transitionTargetsWithSources.push({
            targetPTDVId,
            targetNodeId: edge.target,
            sourcePTDVIds: [sourcePTDVId],
          });
        }
      }
    }
  }

  return transitionTargetsWithSources;
}

/**
 * Validates that no transition that is being configured duplicates another pre-existing transition (optional or planned).
 * In SINGLE_TRANSITION_CANVAS mode the flowchart parameter represents the
 * transition being configured, so we derive the target PTDV and source PTDVs from its
 * nodes and control-flow edges and check them against other transitions.
 *
 * Note that this only applies to optional transitions that are being drawn - if a planned transition is a duplicate of another planned transition,
 * it should be handled by mirroring, and if a planned transition is a duplicate of an optional transition,
 * that should be handled by auto-promotion.
 */
export function ensureTransitionIsNotDuplicate(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  const DUPLICATE_DEFAULT_ERROR_MESSAGE =
    'This transition already exists on the main canvas. Transitions cannot duplicate any existing transition.';

  const {transitions} = validationContext;
  if (transitions == null || Object.keys(transitions).length === 0) {
    return null;
  }

  // flowchart.id is the ID of the transition being configured. We exclude it from the comparison.
  const otherTransitions = Object.values(transitions).filter((t) => t.id !== flowchart.id);
  if (otherTransitions.length === 0) {
    return null;
  }

  const transitionTargetsAndSources = getTransitionTargetsAndSources(flowchart);
  // In the single transition canvas, we assume at most one target node. Multiple targets will be handled by the ensureTransitionHasSingleTarget rule.
  if (transitionTargetsAndSources.length === 0 || transitionTargetsAndSources.length > 1) {
    return null;
  }
  const transition = transitionTargetsAndSources[0];
  const uniqueSourcePTDVIds = [...new Set(transition.sourcePTDVIds)];

  const hasDuplicate = otherTransitions.some(
    (otherTransition) =>
      transition.targetPTDVId === otherTransition.targetPTDVId &&
      uniqueSourcePTDVIds.length === otherTransition.sourcePTDVIds.length &&
      uniqueSourcePTDVIds.every((s) => otherTransition.sourcePTDVIds.includes(s))
  );

  if (hasDuplicate) {
    return [
      {
        errorMessage: DUPLICATE_DEFAULT_ERROR_MESSAGE,
        errorType: ProcessFlowchartErrorType.TRANSITION_IS_DUPLICATE,
        blocksPersistence: true,
        enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
        object: {
          clientId: flowchart.id,
          type: ProcessFlowchartObjectType.TRANSITION,
        },
      },
    ];
  }

  return null;
}

/**
 * In the single transition canvas, a transition must have exactly one target node.
 */
export function ensureTransitionHasSingleTarget(
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  const transitionTargetsWithSources = getTransitionTargetsAndSources(flowchart);

  if (transitionTargetsWithSources.length > 1) {
    return transitionTargetsWithSources.map(({targetNodeId}) => ({
      errorMessage:
        'A transition can only have one target node. Please remove a target node from the transition.',
      errorType: ProcessFlowchartErrorType.TRANSITION_HAS_MULTIPLE_TARGETS,
      blocksPersistence: true,
      enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
      object: {
        clientId: targetNodeId,
        type: ProcessFlowchartObjectType.NODE,
      },
    }));
  }

  return null;
}

/**
 * In the single transition canvas, when merging transitions are not enabled,
 * a transition must have exactly one source node.
 */
export function ensureTransitionHasSingleSource(
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  if (BenchlingConfig.WORKFLOWS_ENABLE_MERGING_IN_HORIZONTAL_FLOWCHART) {
    return null;
  }

  const sourceNodeIds = new Set<string>();
  for (const edge of flowchart.edges) {
    if (edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
      sourceNodeIds.add(edge.source);
    }
  }

  if (sourceNodeIds.size > 1) {
    return [
      {
        errorMessage:
          'Merging transitions are currently not enabled. Please remove a source node from the transition.',
        errorType: ProcessFlowchartErrorType.TRANSITION_HAS_MULTIPLE_SOURCES,
        blocksPersistence: true,
        enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
        object: {
          clientId: flowchart.id,
          type: ProcessFlowchartObjectType.TRANSITION,
        },
      },
    ];
  }

  return null;
}

export function getSourceAndTargetFieldsForDataFlowEdge(
  edge: DataFlowEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): {sourceField: FieldDefinitionNode; targetField: FieldDefinitionNode} | null {
  // Get source and target nodes
  const sourceNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  const targetNode = getFlowchartNodeFromClientId(flowchart, edge.target);

  if (!sourceNode || !targetNode) {
    return null;
  }

  if (!isTaskSchemaNode(sourceNode) || !isTaskSchemaNode(targetNode)) {
    return null;
  }
  if (!sourceNode.taskDefinitionVersionSpec || !targetNode.taskDefinitionVersionSpec) {
    return null;
  }

  // Extract full field information from handles
  // For MAP_FROM_START edges, the source field comes from the root node's input field definitions,
  // not the source node's field definitions
  const sourceHandleInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  let sourceField: FieldDefinitionNode | null;

  if (sourceHandleInfo.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
    // Find root node to get the field definition
    const rootNode =
      flowchart.nodes.find((node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) ??
      flowchart.externalRootNodeStaticData;
    if (!rootNode || !isTaskSchemaNode(rootNode) || !rootNode.taskDefinitionVersionSpec) {
      return null;
    }

    const fieldDef = rootNode.taskDefinitionVersionSpec.inputFieldDefinitions?.find(
      (field) => field.fieldClientIdentifier === sourceHandleInfo.fieldClientId
    );
    sourceField = (fieldDef?.fieldDefinition as FieldDefinitionNode) ?? null;
  } else {
    sourceField = getFieldFromHandle(edge.sourceHandle, sourceNode);
  }

  const targetField = getFieldFromHandle(edge.targetHandle, targetNode);
  if (!sourceField || !targetField) {
    return null;
  }
  return {sourceField, targetField};
}

export function ensureDataFlowEdgeFieldTypesAreValid(
  edge: DataFlowEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  // If a custom mapping is configured, we assume the lookup handles field compatibility
  if (edge.isLookupConfigured) {
    return null;
  }

  const sourceAndTargetFields = getSourceAndTargetFieldsForDataFlowEdge(edge, flowchart);
  if (sourceAndTargetFields == null) {
    return null;
  }

  const compatibilityResult = areFieldDefinitionsCompatible(
    sourceAndTargetFields.sourceField,
    sourceAndTargetFields.targetField
  );

  if (!compatibilityResult.isCompatible) {
    return [
      // Return one error to render the data flow edge as red
      {
        object: {
          clientId: edge.id,
          type: ProcessFlowchartObjectType.EDGE,
        },
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH,
        blocksPersistence: true,
        errorTitle: "Field definitions don't match for one or more mappings.",
        errorMessage:
          "Configure a lookup below, or change the field definitions in the task schema's configuration.",
      },
      // Return another error for the source row / node - this is rendered on the node itself
      {
        object: {
          clientId: getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId,
          type: ProcessFlowchartObjectType.ROW,
        },
        parentObjects: [
          {
            clientId: edge.source,
            type: ProcessFlowchartObjectType.NODE,
          },
        ],
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH,
        blocksPersistence: true,
        errorMessage: compatibilityResult.incompatibilityReason!,
      },
    ];
  }
  return null;
}

export function ensureRequiredFieldsHaveMappings(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  // Only check task schema nodes that are not the root node (which cannot have edges drawn to it)
  if (!isTaskSchemaNode(node) || node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) {
    return null;
  }

  // In the single transition canvas, we only need to check if the target node has unmapped required fields
  // (It's fine if the source node has unmapped required fields)
  if (validationContext.editorMode === FlowchartEditorMode.SINGLE_TRANSITION_CANVAS) {
    const hasIncomingControlFlowEdge = flowchart.edges.some(
      (edge) => edge.target === node.id && edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    if (!hasIncomingControlFlowEdge) {
      return null;
    }
  }
  const nodeWithSchema = node;
  const taskDefinitionVersion = nodeWithSchema.taskDefinitionVersionSpec;
  if (!taskDefinitionVersion) {
    return null;
  }

  // Get the field definitions based on whether node is output node
  const isOutputNode = node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
  const fieldDefinitions = isOutputNode
    ? taskDefinitionVersion.outputFieldDefinitions
    : taskDefinitionVersion.inputFieldDefinitions;
  if (!fieldDefinitions) {
    return null;
  }

  // Find all required fields
  const requiredFields = fieldDefinitions.filter(
    (fieldDef) => fieldDef.fieldDefinition != null && fieldDef.fieldDefinition.isRequired
  );
  if (requiredFields.length === 0) {
    return null;
  }

  // Get all incoming data flow edges for this node
  const incomingDataFlowEdges = flowchart.edges.filter(
    (edge) => edge.target === node.id && edge.type === ProcessFlowchartEdgeType.DATA_FLOW
  ) as Array<DataFlowEdgeForValidation>;

  // Get the set of field IDs that have incoming mappings
  const mappedFieldIds = new Set<string>();
  for (const edge of incomingDataFlowEdges) {
    if (edge.targetHandle) {
      const targetFieldInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
      mappedFieldIds.add(targetFieldInfo.fieldClientId);
    }
  }

  // Find required fields that don't have mappings
  const unmappedRequiredFields = requiredFields.filter(
    (fieldDef) => !mappedFieldIds.has(fieldDef.fieldClientIdentifier)
  );

  // If all fields are mapped, return no errors
  if (unmappedRequiredFields.length === 0) {
    return null;
  }

  // Create validation errors for unmapped required fields
  const errors: Array<ProcessFlowchartValidationError> = [];
  // Add individual errors for each unmapped required field (these show up as row-level errors)
  for (const fieldDef of unmappedRequiredFields) {
    errors.push({
      object: {
        clientId: fieldDef.fieldClientIdentifier,
        type: ProcessFlowchartObjectType.ROW,
      },
      parentObjects: [
        {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
      ],
      errorType: ProcessFlowchartErrorType.NODE_MISSING_REQUIRED_PATH,
      blocksPersistence: true,
      errorMessage: `Required field '${fieldDef.fieldDefinition.name}' must have a mapping.`,
    });
  }

  return errors;
}
